/**
 * Shopper requests — what a shopper asked for at the end of a visualizer or
 * chat session (a sample, a showroom visit, a quote, a call back, an order),
 * and the vendor's next-step settings and stores.
 *
 * Mirrors pms-api-v2 `pms-shopper-requests` (vendorView, next-steps.ts,
 * nearest-store.ts). Kept field for field so the screens never transform.
 */

export const REQUEST_KINDS = ['sample', 'visit', 'quote', 'callback', 'buy'] as const;
export type RequestKind = (typeof REQUEST_KINDS)[number];

export const REQUEST_STATUSES = ['new', 'contacted', 'scheduled', 'done', 'cancelled'] as const;
export type RequestStatus = (typeof REQUEST_STATUSES)[number];

/** Still needs the vendor: what the "Open" filter shows. */
export const OPEN_STATUSES: RequestStatus[] = ['new', 'contacted', 'scheduled'];

export const VISIT_SLOTS = ['morning', 'afternoon', 'evening'] as const;
export type VisitSlot = (typeof VISIT_SLOTS)[number];

export const CONTACT_CHANNELS = ['whatsapp', 'email', 'phone'] as const;
export type ContactChannel = (typeof CONTACT_CHANNELS)[number];

export type RequestSource = 'visualizer' | 'smart-assist' | 'shopper-chat' | 'showroom-qr' | 'share' | 'saved-room' | 'mcp';

export interface RequestContact {
    name?: string;
    email?: string;
    phone?: string;
    preferred?: ContactChannel;
    /** Digits for wa.me, worked out server-side; absent when the number can't be placed. */
    whatsapp?: string;
}

export interface RequestItem {
    productUuid: string;
    sku?: string;
    title: string;
    thumbnailUrl?: string;
    surface?: string;
    areaM2?: number;
    quantity?: number;
    unit?: string;
    boxes?: number;
    unitPrice?: number;
    total?: number;
}

export interface RequestStore {
    id: string;
    name: string;
    address?: string;
    distanceKm?: number;
}

export interface RequestView {
    uuid: string;
    kind: RequestKind;
    status: RequestStatus;
    contact: RequestContact;
    leadId: string | null;
    visitorId: string | null;
    threadUuid: string | null;
    spaceId: string | null;
    sharingId: string | null;
    /** The shopper's own room photo. */
    roomImageUrl: string | null;
    /** The render with the product in it. */
    renderImageUrl: string | null;
    /** False: the shopper unticked "Include my room picture" — there is none, by their choice. */
    includeRoomImage?: boolean;
    items: RequestItem[];
    total: number | null;
    currency: string | null;
    areaM2: number | null;
    areaRange: [number, number] | null;
    store: RequestStore | null;
    preferredDate: string | null;
    preferredSlot: VisitSlot | null;
    message: string | null;
    source: RequestSource | string;
    consent: { marketing: boolean; at?: string };
    /** The shopper's language (en/fr/es/ar). */
    lang: string | null;
    attribution: { source?: string; medium?: string; campaign?: string; content?: string } | null;
    /** Our team's test, not a real shopper. */
    internal: { reason: string; by?: string } | null;
    handledBy: string | null;
    handledAt: string | null;
    vendorNote: string | null;
    createdAt: string;
    updatedAt: string;
}

export interface RequestDetail extends RequestView {
    lead?: { id: string; email: string; firstSeenAt?: string; lastSeenAt?: string; eventCount?: number };
    others: Array<{ uuid: string; kind: RequestKind; status: RequestStatus; createdAt: string }>;
}

export interface RequestCounts {
    byStatus: Record<RequestStatus, number>;
    openByKind: Record<RequestKind, number>;
}

export interface RequestList {
    items: RequestView[];
    counts: RequestCounts;
    nextBefore: string | null;
}

export interface RequestListQuery {
    status?: RequestStatus[];
    kind?: RequestKind[];
    leadId?: string;
    q?: string;
    limit?: number;
    before?: string;
}

export interface RequestPatch {
    status?: RequestStatus;
    vendorNote?: string;
    /** YYYY-MM-DD */
    preferredDate?: string;
    preferredSlot?: VisitSlot;
}

export interface NextStepActionSettings {
    enabled?: boolean;
    /** A line the shopper reads under the action. */
    note?: string;
}

export interface NextStepSettings {
    enabled?: boolean;
    sample?: NextStepActionSettings & {
        /** null = not stated, 0 = free. */
        price?: number | null;
        currency?: string;
    };
    visit?: NextStepActionSettings & {
        /** 0 = Sunday … 6 = Saturday. */
        days?: number[];
        slots?: VisitSlot[];
        /** 0–30 */
        leadDays?: number;
        /** 1–120 */
        horizonDays?: number;
    };
    quote?: NextStepActionSettings;
    callback?: NextStepActionSettings;
    buy?: NextStepActionSettings;
    channels?: ContactChannel[];
}

export interface ResolvedNextSteps {
    enabled: boolean;
    /** What shoppers actually see, defaults applied. */
    actions: RequestKind[];
    sample: { price: number | null; currency: string | null; free: boolean } | null;
    visit: { days: number[]; slots: VisitSlot[]; leadDays: number; horizonDays: number } | null;
    notes: Partial<Record<RequestKind, string>>;
    channels: ContactChannel[];
}

export interface VendorStore {
    id: string;
    name: string;
    address?: string;
    city?: string;
    postcode?: string;
    /** ISO-2 */
    country?: string;
    lat?: number;
    lng?: number;
    phone?: string;
    email?: string;
    hours?: string;
    bookingUrl?: string;
    /** Samples can be picked up or ordered here. */
    sampleDesk?: boolean;
}

export interface ShopperRequestSettings {
    nextSteps: NextStepSettings;
    resolved: ResolvedNextSteps;
    stores: VendorStore[];
    /** Showrooms the vendor's website names, for "Import from your website". */
    websiteShowrooms: number;
}

export interface StoreImportResult extends ShopperRequestSettings {
    found: number;
    added: number;
    located: number;
}

/** A product's price as A1 stores it on `commerce.price` (absent on most catalogues until priced). */
export interface ProductPrice {
    amount: number;
    currency: string;
    unit: 'm2' | 'box' | 'piece' | 'unit' | 'linear_m' | 'roll';
}
